// @ts-strict-ignore
import {
  IconName,
  StatusValues,
} from '@usealloy/component-library';
import type { InvestigationStateTypes } from '@usealloy/typegen';
import {
  InvestigationStateTypesMap,
} from '@usealloy/typegen';

import { captureExceptionsToSentry, ResponseException } from '~/helpers/exceptionHandler';
import { snakeToCaps } from '~/helpers/formatting';

import {
  HARDCODED_INV_ERRORS,
  INVESTIGATION_REVIEW_ALLOWED_OUTCOMES,
} from './constants';
import apiCalls from '~/helpers/apiCalls';

type GetInvestigationBadgeInfo = (args: {
  investigationOutcome?: string;
  investigationState: InvestigationStateTypes;
}) => {
  icon: IconName;
  label: string;
  status: StatusValues;
} | null;

export const getInvestigationBadgeInfo: GetInvestigationBadgeInfo = ({
  investigationOutcome,
  investigationState,
}) => {
  if (investigationState === InvestigationStateTypesMap.ALERT_REVIEW) {
    return {
      icon: IconName.Review,
      label: snakeToCaps(investigationState),
      status: StatusValues.attention,
    };
  }
  if (investigationState === InvestigationStateTypesMap.CASE_REVIEW) {
    return {
      icon: IconName.Escalate,
      label: snakeToCaps(investigationState),
      status: StatusValues.warning,
    };
  }
  if (investigationOutcome === 'Confirmed Risk') {
    return {
      icon: IconName.ShieldExclamation,
      label: snakeToCaps(investigationOutcome),
      status: StatusValues.danger,
    };
  }
  if (investigationOutcome === 'Dismissed') {
    return {
      icon: IconName.CaseClosed,
      label: snakeToCaps(investigationOutcome),
      status: StatusValues.dismissed,
    };
  }
  // Should never really hit this case but best to have a fallback
  if (
    (
      investigationState === InvestigationStateTypesMap.ALERT_REVIEW_CLOSED
      || investigationState === InvestigationStateTypesMap.CASE_REVIEW_CLOSED
    )
    && !INVESTIGATION_REVIEW_ALLOWED_OUTCOMES.includes(investigationOutcome)
  ) {
    return {
      icon: IconName.CaseClosed,
      label: snakeToCaps(investigationState),
      status: StatusValues.neutral,
    };
  }
  return null;
};

export const getErrorMessage = (err: unknown): string => {
  if (err instanceof ResponseException && err.responseError?.message) {
    return err.responseError.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Something went wrong. Please try again or contact support.';
};

export const investigationsErrorHandler = ({
  error,
  extra = {},
  message = null,
}) => {
  const { message: errorMessage } = error;
  // Filter out permissions or 404 errors from showing in Sentry
  if (
    errorMessage.includes('permission')
    || errorMessage.includes('could not be found')
    || HARDCODED_INV_ERRORS.has(errorMessage)
  ) {
    captureExceptionsToSentry(
      error,
      {
        ...extra,
        message,
      },
      'warning',
    );
  } else {
    captureExceptionsToSentry(
      error,
      {
        ...extra,
        message,
      },
    );
  }
};

export const pollDataExport = async ({
  exportToken,
  typhonDomain,
}) => {
  let exportResponse = await apiCalls.dataExports.getDataExportDetails({
    typhonDomain,
    exportToken,
  });
  let data = await exportResponse.json();
  let pollCount = 50;
  /* eslint-disable no-await-in-loop */
  while ((data.status === undefined || data.status === 'processing') && pollCount > 0) {
    pollCount -= 1;
    // eslint-disable-next-line no-promise-executor-return
    await new Promise(r => setTimeout(r, 3000));
    exportResponse = await apiCalls.dataExports.getDataExportDetails({
      typhonDomain,
      exportToken,
    });
    data = await exportResponse.json();
  }

  if (data.status === 'success') {
    return apiCalls.dataExports.downloadDataExport({
      typhonDomain,
      exportToken,
    });
  }
  // status => failure or polled enough number of times
  throw Error('CSV export failure');
};

export type OutcomeMeta = {
  escalated_investigation_status?: boolean;
  investigation_status?: boolean;
};

/**
 * Normalize and group investigation statuses with consistent ordering and sub-grouping.
 * - alert_review → base group (rank 0)
 * - case_review or escalated_investigation_status → subGroup 'Escalated' (rank 1)
 * - investigation_status → base group (rank 0)
 * - everything else → subGroup 'Outcomes' (rank 2)
 */
export const formatInvestigationStatuses = (
  statuses: string[],
  outcomeMetaByName: Map<string, OutcomeMeta>,
  options: {
    includeOutcomes?: boolean;
    baseLabel?: string;
    escalatedLabel?: string;
    outcomesLabel?: string;
  } = {},
): Array<{ value: string; subGroup?: string }> => {
  const {
    includeOutcomes = false,
    baseLabel = '',
    escalatedLabel = 'Escalated',
    outcomesLabel = 'Outcomes',
  } = options;

  const mapped = (statuses || []).map(status => {
    const originalStatus = status || '';
    // normalize strings
    const key = originalStatus.toLowerCase().trim().replace(/[_\s-]+/g, '_');
    if (key === 'alert_review') {
      return { value: originalStatus, rank: 0 };
    }
    if (key === 'case_review') {
      return { value: originalStatus, subGroup: escalatedLabel, rank: 1 };
    }
    const meta = outcomeMetaByName.get(key)
      || outcomeMetaByName.get(originalStatus.toLowerCase().trim());
    if (meta) {
      if (meta.escalated_investigation_status) {
        return { value: originalStatus, subGroup: escalatedLabel, rank: 1 };
      }
      if (meta.investigation_status) {
        return { value: originalStatus, rank: 0 };
      }
      // not a status → outcome
      return { value: originalStatus, subGroup: outcomesLabel, rank: 2 };
    }
    return { value: originalStatus, subGroup: outcomesLabel, rank: 2 };
  });

  const getSubGroupByRank = (rank: number): string => {
    if (rank === 0) return baseLabel;
    if (rank === 1) return escalatedLabel;
    return outcomesLabel;
  };

  const withLabels = mapped.map(item => ({
    ...item,
    subGroup: getSubGroupByRank(item.rank),
  }));

  const filtered = includeOutcomes
    ? withLabels
    : withLabels.filter(item => item.rank !== 2);

  return filtered
    .sort((a, b) => (a.rank - b.rank) || String(a.value).localeCompare(String(b.value)))
    .map(({ rank, ...rest }) => rest);
};
